import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CaptureChecklistSnapshotDto,
  ChecklistItem,
  ChecklistSnapshotDetail,
  ChecklistSnapshotSummary,
  UpdateChecklistItemDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

/** Step 5.1 legal checklist — auto-derived state merged with manual overrides. */
export async function getChecklist(fileId: string): Promise<ChecklistItem[]> {
  const { data } = await api.get<ChecklistItem[]>(`/files/${fileId}/checklist`);
  return data;
}

export async function updateChecklistItem(
  fileId: string,
  key: string,
  dto: UpdateChecklistItemDto,
): Promise<ChecklistItem[]> {
  const { data } = await api.patch<ChecklistItem[]>(`/files/${fileId}/checklist/${key}`, dto);
  return data;
}

export const checklistKeys = {
  byFile: (fileId: string) => ['checklist', fileId] as const,
};

export function useChecklist(fileId: string) {
  return useQuery({
    queryKey: checklistKeys.byFile(fileId),
    queryFn: () => getChecklist(fileId),
    enabled: !!fileId,
  });
}

export function useUpdateChecklistItem(fileId: string) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: ({ key, dto }: { key: string; dto: UpdateChecklistItemDto }) =>
      updateChecklistItem(fileId, key, dto),
    // The endpoint returns the whole recomputed checklist.
    onSuccess: (items) => {
      qc.setQueryData(checklistKeys.byFile(fileId), items);
      toast.success('gov.checklist.savedToast');
    },
    onError: (err) => toast.error(err, 'gov.checklist.saveError'),
  });
}

// ── Versions ────────────────────────────────────────────────────────────────
//
// The live checklist re-derives its auto items from the file as it stands, so a
// version is the only way to see what it said when a past decision was taken.
// Versions are append-only; there is no update or delete.

export const checklistSnapshotKeys = {
  byFile: (fileId: string) => ['checklist-snapshots', fileId] as const,
  one: (fileId: string, version: number) => ['checklist-snapshot', fileId, version] as const,
};

export async function listChecklistSnapshots(fileId: string): Promise<ChecklistSnapshotSummary[]> {
  const { data } = await api.get<ChecklistSnapshotSummary[]>(
    `/files/${fileId}/checklist/snapshots`,
  );
  return data;
}

export function useChecklistSnapshots(fileId: string) {
  return useQuery({
    queryKey: checklistSnapshotKeys.byFile(fileId),
    queryFn: () => listChecklistSnapshots(fileId),
    enabled: !!fileId,
  });
}

export function useChecklistSnapshot(fileId: string, version: number | null) {
  return useQuery({
    queryKey: checklistSnapshotKeys.one(fileId, version ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ChecklistSnapshotDetail>(
        `/files/${fileId}/checklist/snapshots/${version}`,
      );
      return data;
    },
    // A frozen record never changes, so it is cached indefinitely.
    staleTime: Infinity,
    enabled: !!fileId && version !== null,
  });
}

export function useCaptureChecklistSnapshot(fileId: string) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (dto: CaptureChecklistSnapshotDto) => {
      const { data } = await api.post<ChecklistSnapshotDetail>(
        `/files/${fileId}/checklist/snapshots`,
        dto,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: checklistSnapshotKeys.byFile(fileId) });
      toast.success('gov.checklist.versions.capturedToast');
    },
    onError: (err) => toast.error(err, 'gov.checklist.versions.captureError'),
  });
}
